import { supabase } from '../lib/supabase'
import type { Deliverable, Project } from '../types/portal'
import { normaliseStatus } from './deliverables'

/**
 * Loads the genuine projects and deliverables from Supabase.
 *
 * The database holds each project's identity (client, name, slug) and its
 * deliverables (key, title, version). Fields the schema does not yet carry —
 * stage, status, visibility, Drive links, shoot details — start blank and are
 * maintained in the session until columns exist for them. Nothing is invented,
 * and no rows are written or modified here.
 */

function warn(what: string, error: { message: string } | null) {
  if (error) console.warn(`[supabase] ${what} failed: ${error.message}`)
}

function text(value: unknown): string {
  return value === null || value === undefined ? '' : String(value)
}

/** The project-level shoot information, as saved by staff. */
function storedSchedule(row: Record<string, unknown>) {
  return {
    nextShootDate: text(row.next_shoot_date).slice(0, 10),
    nextShootTime: text(row.next_shoot_time),
    location: text(row.shoot_location),
  }
}

function storedDeliverable(row: Record<string, unknown>): Deliverable {
  return {
    id: String(row.deliverable_key ?? ''),
    name: String(row.title ?? 'Untitled'),
    type: (String(row.category ?? 'Video') || 'Video') as Deliverable['type'],
    quantity: '',
    /* Legacy stored values are mapped forward, so old rows stay valid. */
    status: normaliseStatus(String(row.status ?? '')),
    version: String(row.version ?? ''),
    versionDate: '—',
    runtime: '—',
    driveUrl: '',
    audioUrl: '',
    internalNote: '',
    visibleToClient: Boolean(row.visible_to_client),
    notes: [],
    changeRequests: [],
    versions: [],
  }
}

/**
 * One project by its real id — used on the client side once join_project has
 * granted access. Reads the project's identity and its deliverables directly,
 * so nothing depends on being able to list every project.
 */
export async function fetchProjectById(projectId: string): Promise<Project | null> {
  if (!supabase || !projectId) return null

  /* Selected in full so the shoot columns are picked up wherever they exist,
     without the query failing on a schema that predates them. */
  const { data: row, error } = await supabase
    .from('projects')
    .select('*')
    .eq('id', projectId)
    .maybeSingle()
  warn('client project load', error)
  if (!row) return null

  const { data: deliverableRows, error: deliverableError } = await supabase
    .from('deliverables')
    .select(
      'project_id, deliverable_key, title, version, category, status, visible_to_client, sort_order',
    )
    .eq('project_id', projectId)
    .order('sort_order', { ascending: true })
  warn('client deliverable load', deliverableError)

  return {
    id: String(row.id),
    slug: String(row.slug ?? ''),
    client: String(row.client_name ?? ''),
    name: String(row.project_name ?? ''),
    type: '',
    description: '',
    status: String(row.status ?? ''),
    ...storedSchedule(row),
    clientContact: '',
    producer: '',
    updated: '',
    deliverables: (deliverableRows ?? []).map((item: Record<string, unknown>) =>
      storedDeliverable(item),
    ),
    shoots: [],
    team: [],
    archived: String(row.status ?? 'active') === 'archived',
  }
}

/** One project by its link slug — used when a client opens a project link. */
export async function fetchProjectBySlug(slug: string): Promise<Project | null> {
  const all = await fetchProjects()
  return all.find((item) => item.slug === slug) ?? null
}

export async function fetchProjects(): Promise<Project[]> {
  if (!supabase) return []

  const { data: projectRows, error } = await supabase.from('projects').select('*')
  warn('project list', error)
  if (!projectRows?.length) return []

  const { data: deliverableRows, error: deliverableError } = await supabase
    .from('deliverables')
    .select(
      'project_id, deliverable_key, title, version, category, status, visible_to_client, sort_order',
    )
    .order('sort_order', { ascending: true })
  warn('deliverable list', deliverableError)

  return projectRows.map((row: Record<string, unknown>) => {
    const id = String(row.id)
    const items = (deliverableRows ?? [])
      .filter((item: Record<string, unknown>) => String(item.project_id) === id)
      .map((item: Record<string, unknown>) => storedDeliverable(item))

    return {
      id,
      slug: String(row.slug ?? ''),
      client: String(row.client_name ?? ''),
      name: String(row.project_name ?? ''),
      type: '',
      description: '',
      status: '',
      ...storedSchedule(row),
      clientContact: '',
      producer: '',
      updated: '',
      deliverables: items,
      shoots: [],
      team: [],
      archived: String(row.status ?? 'active') === 'archived',
    }
  })
}
